import { useTranslation } from "@refinedev/core";

import { Box, Divider, Stack, TextField, Typography } from "@mui/material";

import type {
  BloodPressureParameters,
  ConditionParametersFormData,
  HeartRateParameters,
  WeightParameters,
} from "@/utils/goal-utils";
import {
  DEFAULT_WEIGHT_UNIT,
  WEIGHT_THRESHOLD_DECIMALS,
  type WeightUnit,
  weightUnitLabelKey,
} from "@/utils/weight-units";

import { ConditionCheckboxes } from "./condition-checkboxes";
import { MedicationInfoInput } from "./medication-info-input";

// ============================================================
// ParameterFieldGroup (reusable sub-component)
// ============================================================

interface FieldConfig {
  label: string;
  value: number | undefined;
  errorKey: string;
  onChange: (value: string) => void;
  required?: boolean;
  maxDecimals?: number;
}

interface ParameterFieldGroupProps {
  title: string;
  fields: FieldConfig[];
  fieldErrors: Record<string, string>;
  clearFieldError: (key: string) => void;
  onFieldBlur?: (errorKey: string) => void;
}

const ParameterFieldGroup = ({
  title,
  fields,
  fieldErrors,
  clearFieldError,
  onFieldBlur,
}: ParameterFieldGroupProps) => {
  return (
    <Stack spacing={2.5}>
      <Typography variant="body1" color="primary">
        {title}
      </Typography>
      <Stack direction="row" spacing={2}>
        {fields.map(field => {
          const error = fieldErrors[field.errorKey];
          return (
            <Box key={field.errorKey} flex={1}>
              <TextField
                type="number"
                size="small"
                fullWidth
                required={field.required ?? true}
                label={field.label}
                value={field.value ?? ""}
                onKeyDown={e => {
                  if (field.maxDecimals === 0 && e.key === ".") {
                    e.preventDefault();
                  }
                }}
                onChange={e => {
                  const raw = e.target.value;
                  if (field.maxDecimals !== undefined && raw !== "") {
                    const parts = raw.split(".");
                    if (field.maxDecimals === 0 && raw.includes(".")) {
                      return;
                    }
                    if (parts.length > 1 && parts[1].length > field.maxDecimals) {
                      return;
                    }
                  }
                  field.onChange(raw);
                  if (error) {
                    clearFieldError(field.errorKey);
                  }
                }}
                onFocus={() => {
                  if (error) {
                    clearFieldError(field.errorKey);
                  }
                }}
                onBlur={() => onFieldBlur?.(field.errorKey)}
                error={!!error}
                helperText={error}
              />
            </Box>
          );
        })}
        {fields.length < 3 && <Box flex={1} />}
      </Stack>
    </Stack>
  );
};

// ============================================================
// GoalParametersFields (exported — goals-only fields)
// ============================================================

export interface GoalParametersFieldsProps {
  weight: WeightParameters;
  heartRate: HeartRateParameters;
  bloodPressure: BloodPressureParameters;
  onWeightChange: (weight: WeightParameters) => void;
  onHeartRateChange: (heartRate: HeartRateParameters) => void;
  onBloodPressureChange: (bloodPressure: BloodPressureParameters) => void;
  fieldErrors: Record<string, string>;
  onClearFieldError: (key: string) => void;
  onFieldBlur?: (errorKey: string) => void;
  /** Unit the weight values are expressed in. Defaults to lb. */
  weightUnit?: WeightUnit;
}

export const GoalParametersFields = ({
  weight,
  heartRate,
  bloodPressure,
  onWeightChange,
  onHeartRateChange,
  onBloodPressureChange,
  fieldErrors,
  onClearFieldError,
  onFieldBlur,
  weightUnit = DEFAULT_WEIGHT_UNIT,
}: GoalParametersFieldsProps) => {
  const { translate } = useTranslation();

  const updateWeight = (field: keyof WeightParameters, value: string) => {
    onWeightChange({ ...weight, [field]: value ? Number(value) : undefined });
  };

  const updateHeartRate = (field: keyof HeartRateParameters, value: string) => {
    onHeartRateChange({ ...heartRate, [field]: value ? Number(value) : undefined });
  };

  const updateBloodPressure = (field: keyof BloodPressureParameters, value: string) => {
    onBloodPressureChange({ ...bloodPressure, [field]: value ? Number(value) : undefined });
  };

  return (
    <Stack spacing={2.5}>
      <ParameterFieldGroup
        title={translate(
          "pages.patientOverview.conditionParameters.weightWithUnit",
          { unit: translate(weightUnitLabelKey(weightUnit)) },
          "Weight"
        )}
        fieldErrors={fieldErrors}
        clearFieldError={onClearFieldError}
        onFieldBlur={onFieldBlur}
        fields={[
          {
            label: translate("pages.patientOverview.conditionParameters.minimum", "Minimum"),
            value: weight.minimum,
            errorKey: "weight.minimum",
            onChange: v => updateWeight("minimum", v),
            maxDecimals: WEIGHT_THRESHOLD_DECIMALS[weightUnit],
          },
          {
            label: translate("pages.patientOverview.conditionParameters.maximum", "Maximum"),
            value: weight.maximum,
            errorKey: "weight.maximum",
            onChange: v => updateWeight("maximum", v),
            maxDecimals: WEIGHT_THRESHOLD_DECIMALS[weightUnit],
          },
          {
            label: translate("pages.patientOverview.conditionParameters.maxDelta", "Max Delta"),
            value: weight.maxDelta,
            errorKey: "weight.maxDelta",
            onChange: v => updateWeight("maxDelta", v),
            maxDecimals: WEIGHT_THRESHOLD_DECIMALS[weightUnit],
          },
        ]}
      />

      <ParameterFieldGroup
        title={translate("pages.patientOverview.conditionParameters.pulse", "Pulse (bpm)")}
        fieldErrors={fieldErrors}
        clearFieldError={onClearFieldError}
        onFieldBlur={onFieldBlur}
        fields={[
          {
            label: translate("pages.patientOverview.conditionParameters.minimum", "Minimum"),
            value: heartRate.minimum,
            errorKey: "heartRate.minimum",
            onChange: v => updateHeartRate("minimum", v),
            maxDecimals: 0,
          },
          {
            label: translate("pages.patientOverview.conditionParameters.maximum", "Maximum"),
            value: heartRate.maximum,
            errorKey: "heartRate.maximum",
            onChange: v => updateHeartRate("maximum", v),
            maxDecimals: 0,
          },
        ]}
      />

      <ParameterFieldGroup
        title={translate("pages.patientOverview.conditionParameters.systolicBP", "Systolic Blood Pressure (mmHg)")}
        fieldErrors={fieldErrors}
        clearFieldError={onClearFieldError}
        onFieldBlur={onFieldBlur}
        fields={[
          {
            label: translate("pages.patientOverview.conditionParameters.minimum", "Minimum"),
            value: bloodPressure.systolicMinimum,
            errorKey: "bp.systolicMinimum",
            onChange: v => updateBloodPressure("systolicMinimum", v),
            maxDecimals: 0,
          },
          {
            label: translate("pages.patientOverview.conditionParameters.maximum", "Maximum"),
            value: bloodPressure.systolicMaximum,
            errorKey: "bp.systolicMaximum",
            onChange: v => updateBloodPressure("systolicMaximum", v),
            maxDecimals: 0,
          },
          {
            label: translate("pages.patientOverview.conditionParameters.urgentLow", "Urgent Low"),
            value: bloodPressure.systolicUrgentLow,
            errorKey: "bp.systolicUrgentLow",
            onChange: v => updateBloodPressure("systolicUrgentLow", v),
            maxDecimals: 0,
          },
        ]}
      />

      <ParameterFieldGroup
        title={translate("pages.patientOverview.conditionParameters.diastolicBP", "Diastolic Blood Pressure (mmHg)")}
        fieldErrors={fieldErrors}
        clearFieldError={onClearFieldError}
        onFieldBlur={onFieldBlur}
        fields={[
          {
            label: translate("pages.patientOverview.conditionParameters.minimum", "Minimum"),
            value: bloodPressure.diastolicMinimum,
            errorKey: "bp.diastolicMinimum",
            onChange: v => updateBloodPressure("diastolicMinimum", v),
            maxDecimals: 0,
          },
          {
            label: translate("pages.patientOverview.conditionParameters.maximum", "Maximum"),
            value: bloodPressure.diastolicMaximum,
            errorKey: "bp.diastolicMaximum",
            onChange: v => updateBloodPressure("diastolicMaximum", v),
            maxDecimals: 0,
          },
        ]}
      />
    </Stack>
  );
};

// ============================================================
// ConditionParametersForm (exported — full form)
// ============================================================

export interface ConditionParametersFormProps {
  formData: ConditionParametersFormData;
  onFormDataChange: (data: ConditionParametersFormData) => void;
  fieldErrors: Record<string, string>;
  onClearFieldError: (key: string) => void;
  onFieldBlur?: (errorKey: string) => void;
  /** Unit the weight values are expressed in. Defaults to lb. */
  weightUnit?: WeightUnit;
}

export const ConditionParametersForm = ({
  formData,
  onFormDataChange,
  fieldErrors,
  onClearFieldError,
  onFieldBlur,
  weightUnit,
}: ConditionParametersFormProps) => {
  return (
    <Stack spacing={0}>
      <GoalParametersFields
        weight={formData.weight}
        heartRate={formData.heartRate}
        bloodPressure={formData.bloodPressure}
        onWeightChange={weight => onFormDataChange({ ...formData, weight })}
        onHeartRateChange={heartRate => onFormDataChange({ ...formData, heartRate })}
        onBloodPressureChange={bloodPressure => onFormDataChange({ ...formData, bloodPressure })}
        fieldErrors={fieldErrors}
        onClearFieldError={onClearFieldError}
        onFieldBlur={onFieldBlur}
        weightUnit={weightUnit}
      />

      <Divider sx={{ mt: 2 }} />

      <ConditionCheckboxes
        conditions={formData.conditions}
        onConditionsChange={conditions => onFormDataChange({ ...formData, conditions })}
      />

      {formData.conditions.takesMedicationForWeightGain && (
        <MedicationInfoInput
          value={formData.conditions.medicationInfo ?? ""}
          onChange={value =>
            onFormDataChange({
              ...formData,
              conditions: { ...formData.conditions, medicationInfo: value },
            })
          }
          error={fieldErrors["conditions.medicationInfo"]}
        />
      )}
    </Stack>
  );
};
